import type React from "react";

/**
 * Visual badge of the ticket provider shown on a ticket card: either a real
 * brand logo (`kind: "logo"`, rendered by the matching component) or a colored
 * text pill (`kind: "text"`) as the generic fallback.
 */
export type TicketProviderBadge =
  | {
      "kind": "logo";

      /** Logo asset key — mapped to its component by the card. */
      "logo": string;

      /** User-facing provider name (accessibility label). */
      "label": string;
  } |
  {
      "kind":       "text";
      "label":      string;
      "background": string;
      "color":      string;
      "style"?:     React.CSSProperties;
  };

/**
 * Known provider badges, keyed by the backend provider id (`purchase.provider`).
 *
 * @remarks Adding a provider = one entry here; unknown ids fall back to a
 * neutral text badge instead of crashing the viewer.
 */
const TICKET_PROVIDER_BADGES: Record<string, TicketProviderBadge> = {
    "swiss_cff": {
        "kind":  "logo",
        "logo":  "swiss_cff",
        "label": "SBB CFF FFS",
    },
    "blablacar": {
        "kind":       "text",
        "label":      "BlaBlaCar",
        "background": "#00AFF5",
        "color":      "#FFFFFF",
    },
    "blabladaily": {
        "kind":       "text",
        "label":      "BlaBlaDaily",
        "background": "#2E8B57",
        "color":      "#FFFFFF",
    },
    "rental_motis": {
        "kind":       "text",
        "label":      "Rental",
        "background": "#475569",
        "color":      "#FFFFFF",
    },
    "completion_strategy": {
        "kind":       "text",
        "label":      "Completion",
        "background": "#475569",
        "color":      "#FFFFFF",
    },
};

/**
 * Resolves the badge of a ticket provider.
 *
 * @param provider Backend provider id (e.g. `swiss_cff`).
 * @returns Known provider badge, or a neutral humanized text fallback for unknown ids.
 */
export const resolveTicketProviderBadge = ( provider: string ): TicketProviderBadge => {
    const badge = TICKET_PROVIDER_BADGES[provider];

    if ( badge !== undefined ) {
        return badge;
    }

    const humanized = provider.
        split( "_" ).
        filter( ( part ) => part.length > 0 ).
        map( ( part ) => part.charAt( 0 ).toUpperCase() + part.slice( 1 ) ).
        join( " " );

    return {
        "kind":       "text",
        "label":      humanized === "" ? "Unknown" : humanized,
        "background": "#475569",
        "color":      "#FFFFFF",
    };
};
